import { useMemo, useState } from 'react'
import Seo, { softwareAppSchema } from '../components/Seo'
import Breadcrumbs from '../components/Breadcrumbs'
import Faq from '../components/Faq'
import type { QA } from '../components/Faq'
import ResultSheet from '../components/ResultSheet'
import CalcActions from '../components/CalcActions'
import InfoTip from '../components/InfoTip'
import { Field, SmartField, Segmented } from '../components/Inputs'
import { computeSheetCut, DEFAULT_SHEET_CUT } from '../calculators/sheetCut'
import type { SheetCutInputs } from '../calculators/sheetCut'
import { calcsInCategory } from '../data/calculators'
import { saveSheet, genId } from '../lib/storage'
import { useLocale, localizedHref } from '../i18n/locale'
import { t } from '../i18n/strings'
import { calcName, categoryName } from '../i18n/calculators'

const PATH = '/calculators/sheet-cut/'
const CATEGORY = 'flooring-and-tile'

const TITLE = 'Sheet Cut Calculator — Plywood Cut Layout & Sheets Needed | RenoSheets'
const DESC = 'Free sheet cut layout calculator. Enter stock sheet size, part size and kerf to get parts per sheet, sheets needed and a visual cut diagram. Plywood, MDF, melamine. No signup.'

const FAQS: QA[] = [
  { q: 'How do I figure out how many parts I can cut from a sheet of plywood?', a: 'Divide the sheet length by the part length and the sheet width by the part width — both rounded down to a whole number — then multiply. The calculator does both orientations (part as-is and rotated 90°) and uses whichever yields more parts. It also subtracts the saw kerf from each successive cut so the layout matches what you can actually mark and cut.' },
  { q: 'What is kerf and why does it matter?', a: 'Kerf is the width of the slot the saw blade removes from the wood — typically 1/8 inch (0.125 in) for a standard circular saw blade or 1/16 inch for a fine table-saw blade. Ignore kerf and your last part on a tightly-packed layout will be a hair too narrow. The calculator subtracts kerf between each adjacent cut so the numbers match the real piece of plywood on your sawhorses.' },
  { q: 'Does the calculator solve the full 2D cutting-stock problem?', a: 'No — it uses a simple grid layout (all parts in the same orientation, aligned in rows and columns). For a single uniform part size that is optimal or near-optimal, and the layout is something you can mark out with a tape measure. Mixed part sizes or rotation per part require dedicated nesting software like CutList Optimizer or OptiNest.' },
  { q: 'What is the standard sheet size for plywood?', a: 'In North America, 4 ft × 8 ft (48 in × 96 in) is the universal sheet size for plywood, OSB, MDF, particleboard and most melamine. Baltic birch ships in metric 5 ft × 5 ft (60 in × 60 in) or 1525 × 1525 mm sheets, which the calculator handles — just enter the size you have.' },
  { q: 'Should I add extra sheets for waste?', a: 'The calculator already rounds up to the next whole sheet, so any leftover is your built-in margin. For a project where one bad cut would ruin a sheet — finished melamine, hardwood plywood with a directional grain — buy one extra sheet beyond the calculated count. For utility-grade plywood, the rounded count is usually enough.' },
  { q: 'How do I get clean cuts through plywood without tearout?', a: 'Use a fine-tooth blade (60+ teeth on a circular saw, 80+ on a table saw), score the cut line first with a utility knife, and cut with the good face down on a circular saw or up on a table saw. A piece of painter\'s tape over the cut line on the visible face also reduces tearout on the show side.' },
]

interface CutDiagramProps {
  sheetW: number
  sheetH: number
  partW: number
  partH: number
  perRow: number
  perCol: number
  orientation: 'as-is' | 'rotated'
}

function CutDiagram({ sheetW, sheetH, partW, partH, perRow, perCol, orientation }: CutDiagramProps) {
  const W = 360
  const H = 240
  const padX = 20
  const padY = 25
  if (sheetW <= 0 || sheetH <= 0) {
    return <div className="text-xs text-text-subtle">Enter a sheet size to see the cut layout.</div>
  }
  const maxW = W - padX * 2
  const maxH = H - padY * 2
  const ratio = Math.min(maxW / sheetW, maxH / sheetH)
  const drawSheetW = sheetW * ratio
  const drawSheetH = sheetH * ratio
  const origin = { x: (W - drawSheetW) / 2, y: padY + 10 }
  // If rotated, the parts being laid out are partH × partW (we swap when drawing)
  const pW = (orientation === 'rotated' ? partH : partW) * ratio
  const pH = (orientation === 'rotated' ? partW : partH) * ratio

  const parts = []
  for (let row = 0; row < perCol; row++) {
    for (let col = 0; col < perRow; col++) {
      parts.push({
        x: origin.x + col * pW,
        y: origin.y + row * pH,
        w: pW,
        h: pH,
        idx: row * perRow + col + 1,
      })
    }
  }

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full max-w-md" role="img" aria-label="Sheet cut layout diagram">
      {/* Sheet outline */}
      <rect x={origin.x} y={origin.y} width={drawSheetW} height={drawSheetH} fill="#fef3c7" stroke="#92400e" strokeWidth="2" />
      {/* Parts */}
      {parts.map((p, k) => (
        <g key={k}>
          <rect x={p.x} y={p.y} width={p.w} height={p.h} fill="#dcfce7" stroke="#166534" strokeWidth="1" />
          {p.w > 18 && p.h > 18 && (
            <text x={p.x + p.w / 2} y={p.y + p.h / 2 + 3} textAnchor="middle" fontSize="9" fill="#166534">{p.idx}</text>
          )}
        </g>
      ))}
      <text x={W / 2} y={16} textAnchor="middle" fontSize="11" fill="#0f172a" fontWeight="600">
        {sheetW} × {sheetH} in sheet · {parts.length} parts {orientation === 'rotated' ? '(rotated 90°)' : ''}
      </text>
    </svg>
  )
}

export default function SheetCutCalculator() {
  const locale = useLocale()
  const L = (en: string) => localizedHref(en, locale)
  const tr = (k: string) => t(locale, k)

  const [i, setI] = useState<SheetCutInputs>(DEFAULT_SHEET_CUT)
  const [saved, setSaved] = useState(false)
  const r = useMemo(() => computeSheetCut(i, locale), [i, locale])
  const set = (patch: Partial<SheetCutInputs>) => {
    setI((p) => ({ ...p, ...patch }))
    setSaved(false)
  }

  const onSave = async () => {
    await saveSheet({
      id: genId(),
      type: 'sheet-cut',
      name: `Sheet Cut — ${i.partWidthIn}×${i.partHeightIn} from ${i.sheetWidthIn}×${i.sheetHeightIn}`,
      createdAt: new Date().toISOString(),
      summary: i.mode === 'reverse'
        ? `${r.partsPerSheet}/sheet · ${r.maxPartsFromSheets} parts from ${Math.max(0, Math.floor(i.sheetsHave))} sheets`
        : `${r.partsPerSheet}/sheet · ${r.sheetsNeeded} sheets · ${r.wastePctPerSheet}% waste`,
      lines: r.lines.filter((l) => l.kind === 'consumable').map((l) => ({ label: l.label, qty: l.qty, unit: l.unit })),
    })
    setSaved(true)
  }

  const related = calcsInCategory(CATEGORY).filter((x) => x.slug !== 'sheet-cut')
  const assumptions = i.mode === 'reverse'
    ? `${Math.max(0, Math.floor(i.sheetsHave))} sheets of ${i.sheetWidthIn} × ${i.sheetHeightIn} in · ${i.partWidthIn} × ${i.partHeightIn} in parts · ${i.kerfIn} in kerf · ${r.bestOrientation === 'rotated' ? 'parts rotated 90°' : 'parts as-is'}.`
    : `${i.sheetWidthIn} × ${i.sheetHeightIn} in sheet · ${i.partWidthIn} × ${i.partHeightIn} in parts · ${i.kerfIn} in kerf · ${r.bestOrientation === 'rotated' ? 'parts rotated 90°' : 'parts as-is'}.`

  // Step-by-step "How we got this" content for the ⓘ button on the result card.
  // Mirrors computeSheetCut: try both orientations, parts per row/col use the
  // grid-pack formula floor((sheet+kerf)/(part+kerf)), then sheets = ceil(qty / per sheet).
  const layoutW = r.bestOrientation === 'rotated' ? i.partHeightIn : i.partWidthIn
  const layoutH = r.bestOrientation === 'rotated' ? i.partWidthIn : i.partHeightIn
  const buildExplain = (): React.ReactNode => (
    <ol className="list-decimal space-y-1 pl-5">
      <li>
        {locale === 'fr'
          ? `Meilleure orientation: ${r.bestOrientation === 'rotated' ? 'pivotées 90°' : 'telles quelles'} (pièce ${layoutW} × ${layoutH} po)`
          : `Best orientation: ${r.bestOrientation === 'rotated' ? 'rotated 90°' : 'as-is'} (part ${layoutW} × ${layoutH} in)`}
      </li>
      <li>
        {locale === 'fr'
          ? `Par rangée = arrondi.inf((${i.sheetWidthIn}+${i.kerfIn}) ÷ (${layoutW}+${i.kerfIn}))`
          : `Per row = floor((${i.sheetWidthIn}+${i.kerfIn}) ÷ (${layoutW}+${i.kerfIn}))`}
        {' '}= <strong>{r.partsPerRow}</strong>
      </li>
      <li>
        {locale === 'fr'
          ? `Par colonne = arrondi.inf((${i.sheetHeightIn}+${i.kerfIn}) ÷ (${layoutH}+${i.kerfIn}))`
          : `Per col = floor((${i.sheetHeightIn}+${i.kerfIn}) ÷ (${layoutH}+${i.kerfIn}))`}
        {' '}= <strong>{r.partsPerCol}</strong>
      </li>
      <li>
        {locale === 'fr'
          ? `Pièces par panneau = ${r.partsPerRow} × ${r.partsPerCol}`
          : `Parts per sheet = ${r.partsPerRow} × ${r.partsPerCol}`}
        {' '}= <strong>{r.partsPerSheet}</strong>
      </li>
      {i.mode === 'forward' ? (
        <>
          <li>
            {locale === 'fr'
              ? `Panneaux = arrondi.sup(${i.qtyNeeded} ÷ ${r.partsPerSheet})`
              : `Sheets = ceil(${i.qtyNeeded} ÷ ${r.partsPerSheet})`}
            {' '}= <strong>{r.sheetsNeeded} {locale === 'fr' ? (r.sheetsNeeded === 1 ? 'panneau' : 'panneaux') : (r.sheetsNeeded === 1 ? 'sheet' : 'sheets')}</strong>
          </li>
          <li>
            {locale === 'fr'
              ? `Coupes totales = ${r.sheetsNeeded} × ${r.partsPerSheet} · reste ${r.leftoverParts}`
              : `Total cut = ${r.sheetsNeeded} × ${r.partsPerSheet} · ${r.leftoverParts} leftover`}
          </li>
        </>
      ) : (
        <li>
          {locale === 'fr'
            ? `Pièces = ${Math.max(0, Math.floor(i.sheetsHave))} panneaux × ${r.partsPerSheet}`
            : `Parts = ${Math.max(0, Math.floor(i.sheetsHave))} sheets × ${r.partsPerSheet}`}
          {' '}= <strong>{r.maxPartsFromSheets} {locale === 'fr' ? (r.maxPartsFromSheets === 1 ? 'pièce' : 'pièces') : (r.maxPartsFromSheets === 1 ? 'part' : 'parts')}</strong>
        </li>
      )}
      <li>
        {locale === 'fr'
          ? `Perte par panneau`
          : `Waste per sheet`}: <strong>{r.wastePctPerSheet}%</strong>
      </li>
    </ol>
  )

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <Seo title={TITLE} description={DESC} path={PATH} schema={softwareAppSchema(calcName('sheet-cut', 'Sheet Cut Calculator', locale), DESC, localizedHref(PATH, locale))} />
      <Breadcrumbs
        items={[
          { name: tr('crumb.home'), href: L('/') },
          { name: categoryName(CATEGORY, 'Flooring & Tile', locale), href: L(`/calculators/${CATEGORY}/`) },
          { name: calcName('sheet-cut', 'Sheet Cut Calculator', locale), href: L(PATH) },
        ]}
      />

      <h1 className="text-3xl">Sheet Cut Calculator</h1>
      <p className="mt-1 max-w-content text-text-muted">Enter your stock sheet size, the part you need to cut, and the saw kerf — get parts per sheet, total sheets to buy, and a visual cut layout you can mark out by hand.</p>

      <div className="mt-6 grid gap-6 lg:grid-cols-5">
        <form className="rounded-xl border border-border bg-surface p-5 lg:col-span-3" onSubmit={(e) => e.preventDefault()}>
          {/* Goal: forward (parts I need → sheets to buy) vs reverse (sheets I
              have → parts I can cut). */}
          <Segmented
            label="What do you want?"
            value={i.mode}
            onChange={(v) => set({ mode: v as 'forward' | 'reverse' })}
            options={[{ v: 'forward', l: 'Parts I need' }, { v: 'reverse', l: 'Sheets I have' }]}
          />

          <div className="mt-4 rounded-md border border-border p-3">
            <p className="mb-1 text-sm font-medium text-text-body">
              Sheet stock size
              <InfoTip text="Width × length of the sheet you are buying. Default is 48 × 96 in (a 4 × 8 ft sheet of plywood)." />
            </p>
            <div className="grid grid-cols-2 gap-2">
              <SmartField label="Width" imperialValue={i.sheetWidthIn} onChangeImperial={(n) => set({ sheetWidthIn: n })} imperialUnit="in" step={1} />
              <SmartField label="Length" imperialValue={i.sheetHeightIn} onChangeImperial={(n) => set({ sheetHeightIn: n })} imperialUnit="in" step={1} />
            </div>
            <input type="range" min={12} max={120} step={1} value={i.sheetWidthIn} onChange={(e) => set({ sheetWidthIn: parseFloat(e.target.value) })} className="mt-2 w-full accent-brand" aria-label="Sheet width slider" />
            <input type="range" min={12} max={120} step={1} value={i.sheetHeightIn} onChange={(e) => set({ sheetHeightIn: parseFloat(e.target.value) })} className="mt-2 w-full accent-brand" aria-label="Sheet length slider" />
          </div>

          <div className="mt-4 rounded-md border border-border p-3">
            <p className="mb-1 text-sm font-medium text-text-body">
              Part size
              <InfoTip text="The finished dimensions of one part. The calculator tries both orientations (as-is and rotated 90°) and picks the one that fits more parts per sheet." />
            </p>
            <div className="grid grid-cols-2 gap-2">
              <SmartField label="Width" imperialValue={i.partWidthIn} onChangeImperial={(n) => set({ partWidthIn: n })} imperialUnit="in" step={0.25} />
              <SmartField label="Length" imperialValue={i.partHeightIn} onChangeImperial={(n) => set({ partHeightIn: n })} imperialUnit="in" step={0.25} />
            </div>
            <input type="range" min={1} max={120} step={0.25} value={i.partWidthIn} onChange={(e) => set({ partWidthIn: parseFloat(e.target.value) })} className="mt-2 w-full accent-brand" aria-label="Part width slider" />
            <input type="range" min={1} max={120} step={0.25} value={i.partHeightIn} onChange={(e) => set({ partHeightIn: parseFloat(e.target.value) })} className="mt-2 w-full accent-brand" aria-label="Part length slider" />
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="rounded-md border border-border p-3">
              {i.mode === 'forward' ? (
                <>
                  <p className="mb-1 text-sm font-medium text-text-body">
                    Quantity needed
                    <InfoTip text="Total number of finished parts you need. The calculator rounds up to whole sheets and tells you how many leftovers you will have." />
                  </p>
                  <Field label="Qty" value={i.qtyNeeded} onChange={(n) => set({ qtyNeeded: n })} suffix="parts" step={1} />
                  <input type="range" min={1} max={200} step={1} value={i.qtyNeeded} onChange={(e) => set({ qtyNeeded: parseInt(e.target.value) })} className="mt-2 w-full accent-brand" aria-label="Quantity needed slider" />
                </>
              ) : (
                <>
                  <p className="mb-1 text-sm font-medium text-text-body">
                    Sheets you have
                    <InfoTip text="How many stock sheets you already have on hand. The calculator shows the maximum parts you can cut from them." />
                  </p>
                  <Field label="Sheets" value={i.sheetsHave} onChange={(n) => set({ sheetsHave: n })} suffix="sheets" step={1} />
                  <input type="range" min={1} max={50} step={1} value={i.sheetsHave} onChange={(e) => set({ sheetsHave: parseInt(e.target.value) })} className="mt-2 w-full accent-brand" aria-label="Sheets you have slider" />
                </>
              )}
            </div>
            <div className="rounded-md border border-border p-3">
              <p className="mb-1 text-sm font-medium text-text-body">
                Saw kerf
                <InfoTip text="Width of the cut the saw makes — typically 1/8 in (0.125) for a circular saw, 1/16 in (0.0625) for a fine table-saw blade. Track saws are ~0.085 in." />
              </p>
              <SmartField label="Kerf" imperialValue={i.kerfIn} onChangeImperial={(n) => set({ kerfIn: n })} imperialUnit="in" step={0.0625} />
              <input type="range" min={0} max={0.25} step={0.0625} value={i.kerfIn} onChange={(e) => set({ kerfIn: parseFloat(e.target.value) })} className="mt-2 w-full accent-brand" aria-label="Saw kerf slider" />
            </div>
          </div>

          <div className="mt-5 rounded-lg bg-surface-2 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">Cut summary</p>
            <ul className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <li>Parts per sheet: <strong className="tnum">{r.partsPerSheet}</strong></li>
              <li>Grid: <strong className="tnum">{r.partsPerRow} × {r.partsPerCol}</strong></li>
              {i.mode === 'forward' ? (
                <>
                  <li>Sheets to buy: <strong className="tnum">{r.sheetsNeeded}</strong></li>
                  <li>Total cut: <strong className="tnum">{r.totalPartsCut}</strong></li>
                  <li>Leftover parts: <strong className="tnum">{r.leftoverParts}</strong></li>
                </>
              ) : (
                <>
                  <li>Sheets you have: <strong className="tnum">{Math.max(0, Math.floor(i.sheetsHave))}</strong></li>
                  <li className="col-span-2">Parts you can cut: <strong className="tnum text-brand-text">{r.maxPartsFromSheets}</strong></li>
                </>
              )}
              <li>Waste per sheet: <strong className="tnum">{r.wastePctPerSheet}%</strong></li>
            </ul>
            <p className="mt-2 text-xs text-text-subtle">Best orientation: {r.bestOrientation === 'rotated' ? 'rotate parts 90°' : 'parts as entered'}.</p>
          </div>

          <div className="mt-5 flex flex-col items-center rounded-lg bg-surface-2 p-3">
            <CutDiagram
              sheetW={i.sheetWidthIn}
              sheetH={i.sheetHeightIn}
              partW={i.partWidthIn}
              partH={i.partHeightIn}
              perRow={r.partsPerRow}
              perCol={r.partsPerCol}
              orientation={r.bestOrientation}
            />
            <p className="mt-2 text-xs text-text-subtle">Yellow = full sheet · green = parts. Numbers are the cut order.</p>
          </div>
          <CalcActions onSave={onSave} saved={saved} locale={locale} className="mt-6 border-t border-border pt-4" />
        </form>

        <div className="lg:col-span-2">
          {i.mode === 'forward' ? (
            <ResultSheet
              primary={{ name: 'Sheets to buy', label: `${r.partsPerSheet} parts per sheet`, qty: r.sheetsNeeded, unit: 'sheet', query: 'plywood sheet 4x8 birch' }}
              secondary={{ name: 'Total parts cut', label: `${r.leftoverParts} leftover`, qty: r.totalPartsCut, unit: 'piece', query: 'circular saw blade fine plywood' }}
              lines={r.lines}
              assumptions={assumptions}
              explain={buildExplain()}
            />
          ) : (
            <ResultSheet
              primary={{ name: 'Parts you can cut', label: `${r.partsPerSheet} per sheet × ${Math.max(0, Math.floor(i.sheetsHave))} sheets`, qty: r.maxPartsFromSheets, unit: 'piece', query: 'circular saw blade fine plywood' }}
              secondary={null}
              lines={r.lines}
              assumptions={assumptions}
              explain={buildExplain()}
            />
          )}
        </div>
      </div>

      <article className="mx-auto mt-8 max-w-content text-text-body">
        <h2 className="text-2xl">{locale === 'fr' ? 'Ce qui influence vraiment le nombre de feuilles nécessaire' : 'What actually affects how many sheets you need'}</h2>
        <p className="mt-3">
          {locale === 'fr'
            ? 'La largeur du trait de scie — le matériau que la lame enlève réellement — est facile à oublier et coûteuse à négliger. Une lame de scie circulaire standard a un trait d’environ 1/8 de pouce, et ce « gaspillage » est consommé à chaque coupe, pas seulement sur les bords extérieurs. Sur une feuille avec une douzaine de coupes, le trait de scie seul peut consommer près d’un pouce de matériau utilisable, exactement le genre de manque qui transforme un nombre de feuilles « tout juste suffisant » en un aller-retour au magasin en plein projet. Le sens du grain compte autant pour la solidité que pour l’apparence : le contreplaqué et le MDF ont un grain visible ou structurel, et couper des pièces dans le « mauvais » sens peut affaiblir une tablette ou montrer une surface inégale, alors les dispositions sacrifient parfois un peu d’efficacité matérielle pour respecter le bon sens du grain. Le matériau de la feuille change aussi légèrement le calcul — le contreplaqué et le MDF standards de 4×8 partagent le même format, mais les feuilles entières varient d’une fraction de pouce en taille réelle selon le moulin, ce qui compte surtout quand vous coupez des pièces tout juste à la limite de ce qu’une feuille peut contenir.'
            : 'Kerf width — the material the saw blade actually removes — is easy to ignore and expensive to forget. A standard circular saw blade has a kerf of about 1/8 inch, and that "waste" gets consumed on every single cut, not just the outer edges. On a sheet with a dozen cuts, kerf alone can eat close to an inch of usable material, which is exactly the kind of shortfall that turns a "just enough" sheet count into a mid-project trip back to the store. Grain direction matters for both strength and appearance: plywood and MDF have a visible or structural grain, and cutting parts to run the "wrong" way can weaken a shelf or show an inconsistent surface, so layouts sometimes trade a bit of material efficiency for correct grain orientation. Sheet material also changes the math slightly — standard 4×8 plywood and MDF share the same footprint, but full sheets vary by a fraction of an inch in actual size depending on the mill, which matters most when you\'re cutting parts right at the edge of what a sheet can fit.'}
        </p>
        <h2 className="mt-8 text-2xl">How the cut layout works</h2>
        <p className="mt-3">For a single uniform part size, the most reliable layout is a grid — every part in the same orientation, aligned in rows and columns. The calculator fits as many rows and columns as the sheet allows, accounting for the saw kerf consumed by each cut:</p>
        <pre className="mt-3 overflow-x-auto rounded-lg bg-surface-2 p-3 text-sm text-text">{`parts per row = floor((sheet + kerf) ÷ (part + kerf))
parts per col = floor((sheet + kerf) ÷ (part + kerf))
parts per sheet = row × col
sheets needed = ceil(qty needed ÷ parts per sheet)`}</pre>
        <p className="mt-3">The "+kerf" inside the divide accounts for the first cut consuming no kerf on the outside edge while every subsequent cut adds one kerf width. The calculator tries both orientations (part as-entered and part rotated 90°) and picks whichever fits more parts.</p>

        <h3 className="mt-6 text-xl">When to use a real nesting program</h3>
        <p className="mt-2">A grid layout is optimal for a single part size, but if you are cutting multiple different parts from the same sheet — different drawer sides, shelf widths, custom pieces — a true 2D bin-pack will save material. Free options that handle mixed parts: CutList Optimizer (browser), MaxCut (free tier), Cutlist Plus fx.</p>

        <h3 className="mt-6 text-xl">Common mistakes</h3>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          <li><strong>Forgetting kerf.</strong> A perfect on-paper layout that ignores kerf is short by (n−1) × kerf — about 7/8 in over a tight 8-cut layout.</li>
          <li><strong>Cutting from a single line.</strong> Mark each cut individually from a fresh reference edge. Cumulative measurement error compounds.</li>
          <li><strong>Not accounting for grain.</strong> If you are using plywood with a directional face grain, rotating parts may save material but ruin the look. Lock the orientation when it matters.</li>
          <li><strong>Skipping the sacrificial backer.</strong> A piece of foam or sacrificial sheet underneath supports the cut and prevents tearout on the bottom face.</li>
        </ul>
      </article>

      <Faq items={FAQS} />

      <section className="mt-10">
        <h2 className="text-2xl">Related calculators</h2>
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {related.map((x) => (
            <a
              key={x.slug}
              href={L(`/calculators/${x.slug}/`)}
              className={`rounded-lg border p-3 text-sm transition-colors ${
                x.live
                  ? 'border-border bg-surface-2 font-medium text-brand-text hover:border-brand hover:bg-surface'
                  : 'border-dashed border-border bg-surface-2 text-text-muted'
              }`}
            >
              {calcName(x.slug, x.name, locale)} {x.live ? '' : <span className="text-text-subtle">{tr('calc.soon')}</span>}
            </a>
          ))}
        </div>
      </section>

      <div className="mx-auto mt-8 max-w-content border-t border-border pt-4 text-xs text-text-subtle">
        Reviewed by the RenoSheets team for calculation accuracy. Method: greedy grid bin-pack with kerf compensation, both orientations tested, optimal selected. Suitable for a single uniform part size. Last updated 2026-06-02. Estimates only — verify dimensions before cutting and buy one extra sheet for irreplaceable stock.
      </div>
    </div>
  )
}
